// Subscription plan & feature gating.
// `user_profile.plan` 字段在 DB 已存在；这里集中描述每个 plan 的 limits。
// 业务方调用 checkLimit / checkFeature，避免在 UI / API 散落硬编码。
//
// 档位（2026-08 定稿）：
// - free：无限任务；只限 3 个项目；AI 30 次/月
// - pro：解锁项目数 / Gantt / LLM 周报；AI 200 次/月
// - pro_ai：pro 全部功能 + AI 1000 次/月（重度 AI 用户档）
// 旧的「免费每项目 200 任务」上限已废除——它从未被任何端执行过（无调用点），
// 免费档的唯一数量限制是项目数。

export const PLANS = ['free', 'pro', 'pro_ai'] as const;
export type Plan = (typeof PLANS)[number];

export interface PlanLimits {
  maxProjects: number; // 软上限；超限只阻止"新增"，不动既有
  maxAttachmentMB: number;
  /** AI 调用月配额（/ai/breakdown 与 /ai/parse-task 共享一个计数；服务端执行）。 */
  aiCallsPerMonth: number;
  ganttView: boolean;
  weeklyReportLLM: boolean;
  recurringTasks: boolean;
}

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free: {
    // 与 apps/web (FREE_LIMITS.maxProjects) 和 apps/mobile (pro.ts) 实际执行的 3 对齐——
    // 此前为 5，造成同一「免费项目上限」概念三处不一致。统一为 3。
    maxProjects: 3,
    maxAttachmentMB: 10,
    aiCallsPerMonth: 30,
    ganttView: false,
    weeklyReportLLM: false,
    recurringTasks: true, // 周期性是基础功能，不门控
  },
  pro: {
    maxProjects: 1000,
    maxAttachmentMB: 200,
    aiCallsPerMonth: 200,
    ganttView: true,
    weeklyReportLLM: true,
    recurringTasks: true,
  },
  pro_ai: {
    maxProjects: 1000,
    maxAttachmentMB: 200,
    aiCallsPerMonth: 1000,
    ganttView: true,
    weeklyReportLLM: true,
    recurringTasks: true,
  },
};

export function getPlanLimits(plan: string | null | undefined): PlanLimits {
  return PLAN_LIMITS[(plan as Plan) ?? 'free'] ?? PLAN_LIMITS.free;
}

export function isPlan(value: string | null | undefined): value is Plan {
  return value === 'free' || value === 'pro' || value === 'pro_ai';
}

// 档位高低的**唯一真相源**。新增档位只改这里，调用方不需要动。
//
// Why：`plan === 'pro'` 的裸比较在加了 pro_ai 之后会静默漏档——付了更贵的钱反而
// 掉回免费档的能力（同类字段漏项）。凡是问「这个档有没有付费能力」「哪个档更高」
// 的地方都必须走 isPaidPlan / planRank，不要再写字面量比较。
const PLAN_RANK: Record<Plan, number> = { free: 0, pro: 1, pro_ai: 2 };

/** 档位序数：越大越高。脏值降级为 free(0)。用于「同时有多个订阅来源时取最高档」。 */
export function planRank(plan: string | null | undefined): number {
  return isPlan(plan) ? PLAN_RANK[plan] : 0;
}

/** 是否付费档（pro 或 pro_ai）。取代全仓的 `plan === 'pro'`。 */
export function isPaidPlan(plan: string | null | undefined): boolean {
  return planRank(plan) > 0;
}

/** 两个档里更高的那个（脏值按 free 处理）。 */
export function higherPlan(a: string | null | undefined, b: string | null | undefined): Plan {
  const pa: Plan = isPlan(a) ? a : 'free';
  const pb: Plan = isPlan(b) ? b : 'free';
  return PLAN_RANK[pa] >= PLAN_RANK[pb] ? pa : pb;
}

// ---------------------------------------------------------------------------
// 商店商品 ↔ 档位（RevenueCat / App Store / Play 共用的**唯一**映射表）
// ---------------------------------------------------------------------------
//
// Why 放这里：api（webhook + REST reconcile）、mobile（付费墙分组 / 购买后落档）
// 都要回答「这个商品/权益是哪一档」。两端各写一份 = 又一次 web↔mobile 手工分叉，
// 且加档位时必漏一处（付了 Pro AI 的钱只拿到 Pro）。
//
// 约定：**RC entitlement 标识符与 plan id 同名**（`pro` / `pro_ai`）。RC 侧 Pro AI
// 的商品同时挂 `pro` 和 `pro_ai` 两个 entitlement（降级兜底：任一端还没接线时，
// Pro AI 买家至少是 Pro 而不会掉回 free），所以「同时命中多个」是常态，必须取高档。

/** 商店商品标识符 → 档位。ASC / Play 的商品 id 保持一致（见 AUTO_AI_APP_DOCS）。 */
export const STORE_PRODUCT_PLANS: Readonly<Record<string, Plan>> = {
  'app.doneline.pro.monthly': 'pro',
  'app.doneline.pro.yearly': 'pro',
  'app.doneline.proai.monthly': 'pro_ai',
  'app.doneline.proai.yearly': 'pro_ai',
};

/**
 * 商品标识符 → 档位。未知商品返回 null（**不要**兜底成 pro——未知商品可能是
 * 将来新增的更高档，静默当 pro 会少给权益；调用方按自己的场景决定兜底）。
 * Play 的 base plan 形态 `productId:basePlanId` 也能识别（取冒号前一段）。
 */
export function planFromProductId(productId: string | null | undefined): Plan | null {
  if (typeof productId !== 'string' || productId.length === 0) return null;
  const base = productId.split(':')[0] ?? productId;
  return STORE_PRODUCT_PLANS[base] ?? STORE_PRODUCT_PLANS[productId] ?? null;
}

/**
 * RC entitlement id 列表 → 档位（取最高档）。无付费权益返回 null。
 * 未知 entitlement 一律忽略，绝不当付费档。
 */
export function planFromEntitlements(entitlementIds: readonly string[]): Plan | null {
  let best: Plan | null = null;
  for (const id of entitlementIds) {
    if (!isPlan(id) || id === 'free') continue;
    if (best === null || PLAN_RANK[id] > PLAN_RANK[best]) best = id;
  }
  return best;
}

export type GateReason = 'limit_exceeded' | 'feature_locked';

export interface GateResult {
  allowed: boolean;
  reason?: GateReason;
  limit?: number;
}

export function gateProjectCreate(
  plan: string | null | undefined,
  currentCount: number,
): GateResult {
  const limits = getPlanLimits(plan);
  if (currentCount >= limits.maxProjects) {
    return { allowed: false, reason: 'limit_exceeded', limit: limits.maxProjects };
  }
  return { allowed: true };
}

export function gateGantt(plan: string | null | undefined): GateResult {
  return getPlanLimits(plan).ganttView
    ? { allowed: true }
    : { allowed: false, reason: 'feature_locked' };
}
